import { useState, useEffect } from "react";
import { Button, Table, Modal, Form } from "react-bootstrap";
import { FaTrash, FaSearch, FaFileAlt, FaPlus } from "react-icons/fa";
import { toast } from "react-toastify";
import DashboardLayout from "../components/layout/DashboardLayout";
import ReactPaginate from "react-paginate";
import { PropagateLoader } from "react-spinners";
import { Dropdown } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { FaEye } from "react-icons/fa";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface Document {
  id_document: number;
  document_type: string;
  file_path: string;
}

export default function SupplierDocuments() {
  const [searchParams] = useSearchParams();
  const id_user =
    searchParams.get("id_user") ?? localStorage.getItem("userid") ?? "";

  const [documents, setDocuments] = useState<Document[]>([]);
  const [search, setSearch] = useState("");
  const [column, setColumn] = useState("document_type");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState("ASC");
  const [colum, setColum] = useState("document_type");
  const [loading, setLoading] = useState(false);

  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const [showModal, setShowModal] = useState(false);
  const [documentType, setDocumentType] = useState("");
  const [file, setFile] = useState<File | null>(null);

  /* ================= LOAD ================= */

  const loadDocuments = async () => {
    setLoading(true);

    const res = await fetch(`${backendUrl}/api/supplier-documents/${id_user}`);
    const data = await res.json();

    setDocuments(data);

    setTimeout(() => setLoading(false), 400);
  };

  useEffect(() => {
    loadDocuments();
  }, []);

  /* ================= FILTER ================= */

  const filtered = documents.filter((doc) =>
    doc[column as keyof Document]
      ?.toString()
      .toLowerCase()
      .includes(search.toLowerCase()),
  );

  /* ================= PAGINATION ================= */

  const offset = page * limit;
  const current = filtered.slice(offset, offset + limit);
  const pageCount = Math.ceil(filtered.length / limit);

  /* ================= SORT ================= */

  const handleSortingColum = (col: string) => {
    setLoading(true);

    const newSort = sort === "ASC" ? "DESC" : "ASC";
    setSort(newSort);
    setColum(col);

    const sorted = [...documents].sort((a: any, b: any) => {
      if (a[col] < b[col]) return newSort === "ASC" ? -1 : 1;
      if (a[col] > b[col]) return newSort === "ASC" ? 1 : -1;
      return 0;
    });

    setDocuments(sorted);

    setTimeout(() => setLoading(false), 300);
  };

  /* ================= SELECT ================= */

  const handleSelectRow = (id: number) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === current.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(current.map((d) => d.id_document));
    }
  };

  /* ================= DELETE ================= */

  const deleteDocument = async (id: number) => {
    await fetch(`${backendUrl}/api/supplier-documents/delete/${id}`, {
      method: "DELETE",
    });
    toast.success("Deleted");
    loadDocuments();
  };

  /* ================= UPLOAD ================= */

  const uploadDocument = async () => {
    if (!file) return toast.error("Select file");

    const formData = new FormData();
    formData.append("file", file);
    formData.append("id_user", id_user);
    formData.append("document_type", documentType);

    await fetch(`${backendUrl}/api/supplier-documents/upload`, {
      method: "POST",
      body: formData,
    });

    toast.success("Uploaded");
    setShowModal(false);
    setDocumentType("");
    setFile(null);
    loadDocuments();
  };

  return (
    <div className="container-fluid">
      {/* HEADER */}
      <div className="d-flex justify-content-between mb-3">
        <h4>
          <FaFileAlt /> Documents ({documents.length})
        </h4>

        <div className="d-flex gap-2">
          <Button variant="warning" onClick={() => setShowModal(true)}>
            <FaPlus /> Add Document
          </Button>
        </div>
      </div>
      <div className="d-flex justify-content-between mb-3">
        {/* SEARCH */}
        <div className="col-md-4">
          <div className="input-group">
            <Dropdown>
              <Dropdown.Toggle variant="link">
                <i className="fas fa-chevron-down"></i>
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setColumn("document_type")}>
                  Type
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            <input
              type="text"
              className="form-control"
              placeholder={`Search by ${column}`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <Button variant="secondary" onClick={() => setSearch("")}>
              ✕
            </Button>
          </div>
        </div>

        {/* AFFICHER */}
        <div className="col-md-8 d-flex justify-content-end align-items-center">
          <div className="dataTables_length d-flex align-items-center">
            <label style={{ marginBottom: 0 }}>
              Afficher
              <select
                className="custom-select custom-select-sm form-control form-control-sm ms-2"
                style={{ width: "70px" }}
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(0);
                }}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
                <option value="500">500</option>
              </select>
            </label>
          </div>
        </div>
      </div>

      {/* TABLE */}
      <Table className="dataTable role-table">
        <thead>
          <tr className="ligth ligth-data">
            <th>
              <div className="checkbox d-inline-block">
                <input
                  type="checkbox"
                  className="checkbox-input"
                  checked={
                    selectedRows.length === current.length && current.length > 0
                  }
                  onChange={handleSelectAll}
                />
              </div>
            </th>

            <th
              className="sorting"
              onClick={() => handleSortingColum("document_type")}
            >
              Type
            </th>
            <th
              className="sorting"
              onClick={() => handleSortingColum("file_path")}
            >
              File
            </th>

            <th>Actions</th>
          </tr>
        </thead>

        <tbody className="ligth-body">
          {loading ? (
            <tr>
              <td colSpan={4} className="text-center">
                <PropagateLoader color="#123abc" size={10} />
              </td>
            </tr>
          ) : (
            current.map((doc) => (
              <tr key={doc.id_document}>
                <td>
                  <div className="checkbox d-inline-block">
                    <input
                      type="checkbox"
                      className="checkbox-input"
                      checked={selectedRows.includes(doc.id_document)}
                      onChange={() => handleSelectRow(doc.id_document)}
                    />
                  </div>
                </td>

                <td>{doc.document_type}</td>

               <td>
  {!doc.file_path ? (
    // NO FILE
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "6px 16px",
        borderRadius: "999px",
        fontSize: "13px",
        fontWeight: 600,
        background: "#f3f4f6",
        color: "#9ca3af",
        border: "1px solid #e5e7eb",
      }}
    >
      No file
    </span>
  ) : (
    <a
      href={`${backendUrl}/uploads/documents/${doc.file_path}`}
      target="_blank"
      rel="noreferrer"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        padding: "6px 16px",
        borderRadius: "999px",
        fontSize: "13px",
        fontWeight: 600,
        textDecoration: "none",

        background: "#fff7ed",
        color: "#d97706",
        border: "1px solid #fbbf24",

        transition: "all 0.2s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "#ffedd5";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "#fff7ed";
      }}
    >
      <FaEye size={12} /> View
    </a>
  )}
</td>
                <td>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => deleteDocument(doc.id_document)}
                  >
                    <FaTrash />
                  </Button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {/* PAGINATION */}
      <div className="d-flex justify-content-between">
        <span>
          Displaying {current.length} out of {filtered.length}
        </span>
        <ReactPaginate
          previousLabel={"précédent"}
          nextLabel={"suivant"}
          breakLabel={"..."}
          pageCount={pageCount}
          marginPagesDisplayed={2}
          pageRangeDisplayed={3}
          onPageChange={(d) => setPage(d.selected)}
          containerClassName={"pagination justify-content-end"}
          pageClassName={"page-item"}
          pageLinkClassName={"page-link"}
          previousClassName={"page-item"}
          previousLinkClassName={"page-link"}
          nextClassName={"page-item"}
          nextLinkClassName={"page-link"}
          activeClassName={"active"}
          forcePage={page}
        />
      </div>

      {/* MODAL */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Upload Document</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Type</Form.Label>
              <Form.Control
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mt-3">
              <Form.Control
                type="file"
                onChange={(e) => {
                  const t = e.target as HTMLInputElement;
                  if (t.files) setFile(t.files[0]);
                }}
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button onClick={uploadDocument}>Upload</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
